import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { enableMapSet } from 'immer'
import { Vector3 } from 'three'
import type { Brick, BrickType, BrickColor } from '@models/brick'
import { historyMiddleware, type HistoryState } from '@middleware/historyMiddleware'
import {
  serializeScene,
  deserializeScene,
  downloadJSON,
  generateSaveFilename,
  parseJSONFile,
  type SaveFileData,
} from '@utils/serialization'

// Enable Immer support for Map and Set
enableMapSet()

/**
 * Camera preset types (Task 3.2.5)
 * - isometric: Default isometric view [15, 15, 15]
 * - top: Top-down view [0, 30, 0] looking down
 * - front: Front view [0, 5, 30] looking at origin
 * - side: Side view [30, 5, 0] looking at origin
 */
export type CameraPreset = 'isometric' | 'top' | 'front' | 'side'

/**
 * BrickStore - Central state management for all bricks and placement settings
 *
 * State:
 * - bricks: Map of all placed bricks (keyed by ID)
 * - selectedBrickType: Currently selected brick type for placement
 * - selectedColor: Current color for new bricks
 * - selectedBrickId: ID of currently selected brick (for deletion, rotation, etc.) - Task 3.2.1
 * - previewPosition: Where the preview brick is shown (null when not hovering)
 * - previewRotation: Rotation of preview brick (0, 90, 180, 270) - Task 3.2.8
 * - isPreviewValid: Whether current preview position is valid for placement
 * - isSnappedToStud: Whether preview is snapped to a stud (for visual feedback)
 * - activeCameraPreset: Current active camera preset (Task 3.2.5)
 *
 * Actions:
 * - addBrick: Add a new placed brick to the world
 * - removeBrick: Remove a brick by ID
 * - rotateBrick: Rotate a brick by 90 degrees (cycles 0→90→180→270→0) - Task 3.2.2
 * - rotatePreview: Rotate preview brick by 90 degrees (cycles 0→90→180→270→0) - Task 3.2.8
 * - setSelectedBrickId: Select/deselect a brick (for keyboard shortcuts) - Task 3.2.1
 * - setPreviewPosition: Update preview brick position (for hover effect)
 * - setPreviewValid: Whether this position is valid for placement
 * - setSnappedToStud: Whether preview is snapped to stud (for green highlight)
 * - setSelectedColor: Change the selected color
 * - setSelectedBrickType: Change the selected brick type (future: Task 3.1.1)
 * - setActiveCameraPreset: Change active camera preset (Task 3.2.5)
 * - undo: Undo last action (Task 3.2.4)
 * - redo: Redo previously undone action (Task 3.2.4)
 * - canUndo: Check if undo is available
 * - canRedo: Check if redo is available
 * - saveScene: Serialize and download scene as JSON file (Task 4.2.3)
 * - loadScene: Load scene from JSON file, replacing current scene (Task 4.2.3)
 * - clearScene: Clear all bricks from the scene (Task 4.2.3)
 */
interface BrickStore {
  // State
  bricks: Map<string, Brick>
  selectedBrickType: BrickType
  selectedColor: BrickColor
  selectedBrickId: string | null // ID of selected brick for keyboard shortcuts (Task 3.2.1)
  previewPosition: Vector3 | null // null when mouse not over valid placement area
  previewRotation: number // Rotation of preview brick (0, 90, 180, 270) - Task 3.2.8
  isPreviewValid: boolean // Whether current preview position is valid for placement
  isSnappedToStud: boolean // Whether preview is snapped to a stud connection
  activeCameraPreset: CameraPreset // Current camera preset (Task 3.2.5)

  // Actions
  addBrick: (brick: Brick) => void
  removeBrick: (id: string) => void
  rotateBrick: (id: string) => number // Rotate brick by 90 degrees, return new rotation (Task 3.2.2)
  rotatePreview: () => number // Rotate preview brick by 90 degrees, return new rotation (Task 3.2.8)
  _setRotation: (id: string, rotation: number) => void // Internal: Set brick rotation without recording (for undo/redo)
  setSelectedBrickId: (id: string | null) => void // Task 3.2.1
  setPreviewPosition: (position: Vector3 | null) => void
  setPreviewValid: (isValid: boolean) => void
  setSnappedToStud: (isSnapped: boolean) => void
  setSelectedColor: (color: BrickColor) => void
  setSelectedBrickType: (type: BrickType) => void
  setActiveCameraPreset: (preset: CameraPreset) => void // Task 3.2.5
  // History actions (undo, redo, canUndo, canRedo) are added by historyMiddleware
  // Save/Load actions - Task 4.2.3
  saveScene: () => void // Serialize and download scene as JSON
  loadScene: (fileContent: string) => void // Load scene from JSON file content
  clearScene: () => void // Clear all bricks (used before loading)
}

export const useBrickStore = create<BrickStore & HistoryState>()(
  historyMiddleware(
    immer((set, get) => ({
      // Initial state
      bricks: new Map(),
      selectedBrickType: '1x1', // Start with simplest brick
      selectedColor: { r: 220, g: 20, b: 60 }, // Crimson red (classic LEGO color)
      selectedBrickId: null, // No brick selected initially (Task 3.2.1)
      previewPosition: null,
      previewRotation: 0, // Default rotation 0° (Task 3.2.8)
      isPreviewValid: true, // Assume valid until collision detected
      isSnappedToStud: false, // Not snapped initially
      activeCameraPreset: 'isometric' as CameraPreset, // Default isometric view (Task 3.2.5)

      // Add a placed brick to the world
      addBrick: (brick) => {
        // Create a plain object copy BEFORE adding to state (to avoid frozen objects)
        const plainBrick = JSON.parse(JSON.stringify(brick))

        // Add the brick to state
        set((state) => {
          state.bricks.set(brick.id, brick)
        })

        // Then record action for undo/redo (Task 3.2.4)
        const state = get() as any
        state._recordAction({
          type: 'ADD_BRICK',
          description: `Brick placement (${brick.type})`,
          data: {
            brick: plainBrick
          },
        })
      },

      // Remove a brick from the world
      removeBrick: (id) => {
        // Get brick before removing for undo/redo (Task 3.2.4)
        const state = get() as any
        const brick = state.bricks.get(id)

        if (brick) {
          // Create a plain object copy to avoid Immer frozen object issues
          const plainBrick = JSON.parse(JSON.stringify(brick))

          state._recordAction({
            type: 'REMOVE_BRICK',
            description: `Brick deletion (${brick.type})`,
            data: {
              brick: plainBrick
            },
          })
        }

        set((state) => {
          state.bricks.delete(id)
          // Also deselect if the removed brick was selected
          if (state.selectedBrickId === id) {
            state.selectedBrickId = null
          }
        })
      },

      // Rotate a brick by 90 degrees (Task 3.2.2)
      // Cycles through 0 → 90 → 180 → 270 → 0
      rotateBrick: (id) => {
        let newRotation = 0
        const state = get() as any
        const brick = state.bricks.get(id)

        if (brick) {
          const previousRotation = brick.rotation
          newRotation = (brick.rotation + 90) % 360

          // Record action for undo/redo (Task 3.2.4)
          state._recordAction({
            type: 'ROTATE_BRICK',
            description: `Brick rotation (${previousRotation}° → ${newRotation}°)`,
            data: {
              brickId: id,
              previousRotation,
              newRotation,
            },
          })

          set((state) => {
            const brick = state.bricks.get(id)
            if (brick) {
              brick.rotation = newRotation
            }
          })
        }

        return newRotation
      },

      // Rotate preview brick by 90 degrees (Task 3.2.8)
      // Cycles through 0 → 90 → 180 → 270 → 0
      rotatePreview: () => {
        let newRotation = 0
        set((state) => {
          newRotation = (state.previewRotation + 90) % 360
          state.previewRotation = newRotation
        })
        return newRotation
      },

      // Internal method: Set brick rotation without recording history
      // Used by history middleware for undo/redo
      _setRotation: (id, rotation) => {
        set((state) => {
          const brick = state.bricks.get(id)
          if (brick) {
            brick.rotation = rotation
          }
        })
      },

    // Select/deselect a brick (for keyboard shortcuts - Task 3.2.1)
    setSelectedBrickId: (id) => {
      set((state) => {
        state.selectedBrickId = id
      })
    },

    // Update preview brick position (shown when hovering)
    setPreviewPosition: (position) => {
      set((state) => {
        state.previewPosition = position
      })
    },

    // Update preview validity state (for visual feedback)
    setPreviewValid: (isValid) => {
      set((state) => {
        state.isPreviewValid = isValid
      })
    },

    // Update stud snapping state (for green highlight visual feedback)
    setSnappedToStud: (isSnapped) => {
      set((state) => {
        state.isSnappedToStud = isSnapped
      })
    },

    // Change selected color (will be used by UI in Task 3.1.1)
    setSelectedColor: (color) => {
      set((state) => {
        state.selectedColor = color
      })
    },

    // Change selected brick type (will be used by UI in Task 3.1.1)
    setSelectedBrickType: (type) => {
      set((state) => {
        state.selectedBrickType = type
      })
    },

    // Change active camera preset (Task 3.2.5)
    setActiveCameraPreset: (preset) => {
      set((state) => {
        state.activeCameraPreset = preset
      })
    },

    // Save scene to JSON file (Task 4.2.3)
    saveScene: () => {
      const state = get()

      // Serialize the scene
      const saveData = serializeScene(state.bricks)

      // Generate filename and download
      const filename = generateSaveFilename()
      downloadJSON(saveData, filename)
    },

    // Load scene from JSON file content (Task 4.2.3)
    loadScene: (fileContent) => {
      // Parse and validate JSON
      const saveData = parseJSONFile(fileContent)

      // Deserialize bricks
      const bricks = deserializeScene(saveData)

      // Replace current scene
      set((state) => {
        state.bricks = bricks
        state.selectedBrickId = null // Clear selection
      })

      // Clear undo/redo history after loading (Task 4.2.3)
      const stateWithHistory = get() as any
      if (stateWithHistory._clearHistory) {
        stateWithHistory._clearHistory()
      }
    },

    // Clear all bricks from scene (Task 4.2.3)
    clearScene: () => {
      set((state) => {
        state.bricks = new Map()
        state.selectedBrickId = null
      })

      // Clear undo/redo history when clearing scene
      const stateWithHistory = get() as any
      if (stateWithHistory._clearHistory) {
        stateWithHistory._clearHistory()
      }
    },
  })),
  50 // Max history size (Task 3.2.4)
  )
)
